import * as React from "react";
import { FC, useState } from "react";
import { IImageData } from "../Map/imageProvider";
import { IFilterConfig } from "../Map/config";
import { IPadding, IUpdates, IVisibleLayer } from "../Map/mapProvider";
import { Feature } from "ol";
import { DemoPaddingControl, DemoMapVisibilityControl, DemoLayerVisibilityControl } from "./DemoControls";
import { DemoContextProvider } from "./DemoContext";
import { DemoSearch } from "./DemoSearch";
import { DemoFeatures } from "./DemoFeatures";
import { DemoFilters } from "./DemoFilters";
import { DemoRoutes } from "./DemoRoutes";
import { DemoGame } from "./DemoGame";
import { DemoDestinations } from "./DemoDestinations";

export interface IDemoUIProps {
    /**
     * available filters config
     */
    filtersConfig: IFilterConfig[];
    /**
     * objects (features), filtered according filters.
     * if no filters selected, then all objects (features)
     */
    filteredObjects: Feature[];
    /**
     * objects (features), filtered on text search field change
     * empty array, if no text in search field
     */
    filteredByText: Feature[];
    /**
     * identified object attributes, received on map object click
     */
    identifiedObject: Feature;
    /**
     * object related images data
     */
    objectImageData: IImageData[];
    /**
     * all routes, as array of route main point features
     */
    routeList: Feature[];
    /**
     * all destinations (Mammadaba galamērķi), as array of features
     */
    destinationList: Feature[];
    /**
     * all games, as array of game main point features
     */
    gameList: Feature[];
    /**
     * selected Game, as Game main point feature
     */
    selectedGame: Feature;
    /**
     * game checkpoints, approached in predefined radius around user location
     */
    approachedGameCheckPoints: Feature[];
    /**
     * game checkpoints, that left predefined "leaving" radius around user location
     */
    leftGameCheckPoints: Feature[];
    /**
     * game main feature, when game is completed
     */
    gameCompleted: Feature;
    /**
     * Game checkpoint feature, selected by click on map
     */
    selectedCheckPoint: Feature;
    /**
     * callback, to select Route, pass ROUTEID field value as argument
     */
    onSelectRoute: (routeId: string) => void;
    /**
     * callback, to start Game mode Map, pass true, if use simulator instead of real user location
     */
    onStartGameMode: (simulatorMode: boolean) => void;
    /**
     * callback, to stop Game mode Map
     */
    onStopGameMode: () => void;
    /**
     * select game, by GAMEID
     */
    onSelectGame: (gameId: string) => void;
    /**
     * callback, to start selected Game
     */
    onStartGame: () => void;
    /**
     * callback, to stop selected Game
     */
    onStopGame: () => void;
    /**
     * callback, to mark checkpoint as `Found`
     */
    onSetFound: (feature: Feature) => void;
    /**
     * callback on Search field value change, to get search results/suggestions
     */
    onSearchUpdate: (currentFilters: any, text: string) => void;
    /**
     * callback on Apply search and filters button click, to get objects according search and filter values
     */
    onApplySearchResults: (currentFilters: any, searchFieldValue: string) => void;
    /**
     * callback, on map visibility state change
     * @param isMapVisible boolean, describes visibility state
     */
    onMapToggle: (isMapVisible: boolean) => void;
    /**
     * callback on layer visibility state change
     * @param layers array, describes state of visibility
     */
    setLayerVisibility: (layers: IVisibleLayer[]) => void;
    /**
    /**
     * callback, when ZoomTo button clicked, to zoom to particular object
     * @param graphic is any Feature to zoom to
     */
    onZoomToGraphic: (graphic: Feature) => void;
    /**
     * callback, to update feature attributes
     * @param graphic is particular Feature to update
     * @param updates is object containing attributes to update and new values for them
     */
    onUpdateAttributes: (graphic: Feature, updates: IUpdates) => void;
    /**
     * callback, to show or hide circle for nearby objects filter
     * @param enabled boolean, true if show circle
     */
    onNearbyChange: (enabled: boolean) => void;
    /**
     * callback, to change map center, by adding padding
     */
    onChangePadding?: (padding: IPadding) => void;
    /**
     * callback, to show/hide route minimap
     * @param routeId string, ID of Route minimap to show or hide
     * @param isOn boolean, true if need to show Route minimap
     */
    onToggleRouteMiniMap?: (routeId: string, isOn: boolean) => void;
    /**
     * callback, to select/deselect game checkpoint
     * @param checkPointId string, checkpoint feature OBJECTID
     */
    onSelectGameCheckPoint?: (checkPointId: string) => void;
    /**
     * callback, to show/hide destination minimap
     * @param destinationId string, OBJECTID of destination to show or hide
     * @param isOn boolean, true if need to show destination minimap
     */
    onToggleDestinationMiniMap?: (destinationId: string, isOn: boolean) => void;
    /**
     * callback, to select Destination, pass OBJECTD field value as argument
     */
    onSelectDestination?: (id: string) => void;
}

export const DemoUI: FC<IDemoUIProps> = (props: IDemoUIProps) => {
    const [gameMode, setGameMode] = useState<boolean>(false);

    const onStartGameMode = (simulatorMode: boolean) => {
        setGameMode(true);
        props.onStartGameMode(simulatorMode);
    };
    const onStopGameMode = () => {
        setGameMode(false);
        props.onStopGameMode();
    };

    return (
        <DemoContextProvider>
            <DemoPaddingControl onChangePadding={props.onChangePadding}></DemoPaddingControl>
            <DemoMapVisibilityControl onMapToggle={props.onMapToggle}></DemoMapVisibilityControl>
            {/* <DemoLayerVisibilityControl setLayerVisibility={props.setLayerVisibility}></DemoLayerVisibilityControl> */}

            <DemoGame
                gameMode={gameMode}
                gameList={props.gameList}
                selectedGame={props.selectedGame}
                approachedGameCheckPoints={props.approachedGameCheckPoints}
                leftGameCheckPoints={props.leftGameCheckPoints}
                gameCompleted={props.gameCompleted}
                selectedCheckPoint={props.selectedCheckPoint}
                onStartGameMode={onStartGameMode}
                onStopGameMode={onStopGameMode}
                onSelectGame={props.onSelectGame}
                onStartGame={props.onStartGame}
                onStopGame={props.onStopGame}
                onSetFound={props.onSetFound}
                onSelectCheckPoint={props.onSelectGameCheckPoint}
            ></DemoGame>
            {!gameMode ? (
                <>
                    <DemoRoutes
                        routeList={props.routeList}
                        onSelectRoute={props.onSelectRoute}
                        onToggleRouteMiniMap={props.onToggleRouteMiniMap}
                    ></DemoRoutes>

                    <DemoDestinations
                        destinationList={props.destinationList}
                        onSelectDestination={props.onSelectDestination}
                        onToggleDestinationMiniMap={props.onToggleDestinationMiniMap}
                    ></DemoDestinations>

                    <DemoSearch
                        filteredByText={props.filteredByText}
                        onApplySearchResults={props.onApplySearchResults}
                        onSearchUpdate={props.onSearchUpdate}
                    ></DemoSearch>

                    <DemoFilters
                        filtersConfig={props.filtersConfig}
                        onNearbyChange={props.onNearbyChange}
                    ></DemoFilters>

                    <DemoFeatures
                        filteredObjects={props.filteredObjects}
                        identifiedObject={props.identifiedObject}
                        objectImageData={props.objectImageData}
                        onZoomToGraphic={props.onZoomToGraphic}
                        onUpdateAttributes={props.onUpdateAttributes}
                    ></DemoFeatures>
                </>
            ) : null}
        </DemoContextProvider>
    );
};
